import { useState, useEffect, useCallback } from 'react'
import { api } from '../context/AuthContext'

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtTime = (d) => d ? new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—'

const avatarBg = (str = '') => {
  const c = ['#fde8d8','#dff5e8','#e8f4ff','#fef3d0','#ede8ff','#fce7f3']
  let h = 0; for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h<<5)-h)
  return c[Math.abs(h) % c.length]
}
const initials = (n = '') => n.trim().split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2) || '?'

// ── Role config ───────────────────────────────────────────────────────────────
const ROLE_CONFIG = {
  superadmin: { label:'Super Admin', color:'#7c3aed', bg:'#f5f3ff', icon:'👑', description:'Full platform access. Cannot be restricted.' },
  admin:      { label:'Admin',       color:'#e8440a', bg:'#fff3f0', icon:'🛡',  description:'Manages platform, users, orders, and settings.' },
  moderator:  { label:'Moderator',   color:'#1d4ed8', bg:'#eff6ff', icon:'👁',  description:'Reviews content, handles tickets, limited settings.' },
  support:    { label:'Support',     color:'#0369a1', bg:'#f0f9ff', icon:'🎧', description:'Responds to tickets and manages help center only.' },
}
const rc = (role) => ROLE_CONFIG[role] || { label: role, color:'#6b7280', bg:'#f3f4f6', icon:'👤', description:'' }

// ── Permission categories with all available permissions ──────────────────────
const PERMISSION_GROUPS = [
  {
    group: 'Users',
    icon: '👥',
    permissions: [
      { key:'users.view',        label:'View users',          description:'See user list and profiles' },
      { key:'users.edit',        label:'Edit users',          description:'Modify user details and status' },
      { key:'users.delete',      label:'Delete users',        description:'Permanently remove users' },
      { key:'users.roles',       label:'Change roles',        description:'Assign roles to users' },
    ],
  },
  {
    group: 'Sellers & Shops',
    icon: '🏪',
    permissions: [
      { key:'shops.view',        label:'View shops',          description:'See shop list and details' },
      { key:'shops.approve',     label:'Approve shops',       description:'Approve or suspend seller registrations' },
      { key:'shops.edit',        label:'Edit shops',          description:'Modify shop details and status' },
      { key:'shops.revoke',      label:'Revoke shops',        description:'Permanently revoke shop access' },
    ],
  },
  {
    group: 'Orders',
    icon: '🛍',
    permissions: [
      { key:'orders.view',       label:'View orders',         description:'See all platform orders' },
      { key:'orders.edit',       label:'Update orders',       description:'Change order status' },
      { key:'orders.refund',     label:'Issue refunds',       description:'Process refunds and returns' },
    ],
  },
  {
    group: 'Payments',
    icon: '💰',
    permissions: [
      { key:'payments.view',     label:'View payments',       description:'See transaction history' },
      { key:'payments.export',   label:'Export reports',      description:'Download payout and payment reports' },
      { key:'payments.settings', label:'Payment settings',    description:'Modify fees and payment methods' },
    ],
  },
  {
    group: 'Content',
    icon: '▶',
    permissions: [
      { key:'reels.view',        label:'View clips',          description:'See all clips' },
      { key:'reels.moderate',    label:'Moderate clips',      description:'Hide, delete, or flag clips' },
      { key:'live.view',         label:'View live streams',   description:'Monitor active streams' },
      { key:'live.end',          label:'End live streams',    description:'Force-end streams' },
      { key:'comments.moderate', label:'Moderate comments',   description:'Flag, delete comments and reviews' },
    ],
  },
  {
    group: 'Drivers',
    icon: '🚚',
    permissions: [
      { key:'drivers.view',      label:'View drivers',        description:'See driver list and profiles' },
      { key:'drivers.edit',      label:'Edit drivers',        description:'Modify driver details and status' },
      { key:'drivers.delete',    label:'Remove drivers',      description:'Remove drivers from platform' },
    ],
  },
  {
    group: 'Support & Help',
    icon: '🎫',
    permissions: [
      { key:'tickets.view',      label:'View tickets',        description:'See support tickets' },
      { key:'tickets.reply',     label:'Reply to tickets',    description:'Send replies to user tickets' },
      { key:'tickets.close',     label:'Close tickets',       description:'Resolve and close tickets' },
      { key:'faqs.manage',       label:'Manage FAQs',         description:'Create, edit, delete FAQ articles' },
    ],
  },
  {
    group: 'Platform Settings',
    icon: '⚙',
    permissions: [
      { key:'settings.view',     label:'View settings',       description:'See app configuration' },
      { key:'settings.edit',     label:'Edit settings',       description:'Modify fees, categories, limits' },
      { key:'notifications.send',label:'Send notifications',  description:'Broadcast push notifications' },
      { key:'analytics.view',    label:'View analytics',      description:'Access analytics dashboard' },
      { key:'terms.edit',        label:'Edit legal docs',     description:'Edit terms, privacy policy, etc.' },
      { key:'roles.manage',      label:'Manage roles',        description:'This page — requires superadmin' },
    ],
  },
]

// Default permissions per built-in role
const DEFAULT_PERMS = {
  superadmin: Object.fromEntries(PERMISSION_GROUPS.flatMap(g => g.permissions.map(p => [p.key, true]))),
  admin:      Object.fromEntries(PERMISSION_GROUPS.flatMap(g => g.permissions.map(p => [p.key,
    !['users.delete','roles.manage','payments.settings','terms.edit'].includes(p.key)
  ]))),
  moderator:  Object.fromEntries(PERMISSION_GROUPS.flatMap(g => g.permissions.map(p => [p.key,
    ['users.view','shops.view','orders.view','reels.view','reels.moderate','live.view','live.end','comments.moderate','tickets.view','tickets.reply','tickets.close','faqs.manage','analytics.view'].includes(p.key)
  ]))),
  support:    Object.fromEntries(PERMISSION_GROUPS.flatMap(g => g.permissions.map(p => [p.key,
    ['tickets.view','tickets.reply','tickets.close','faqs.manage','users.view'].includes(p.key)
  ]))),
}

// ── Toggle ────────────────────────────────────────────────────────────────────
function Toggle({ checked, onChange, disabled }) {
  return (
    <div onClick={() => !disabled && onChange(!checked)}
      style={{ width:38, height:21, borderRadius:11, background:checked?'#e8440a':'#e9e9e4', cursor:disabled?'not-allowed':'pointer', position:'relative', transition:'background 0.2s', flexShrink:0, opacity:disabled?0.4:1 }}>
      <div style={{ position:'absolute', top:2.5, left:checked?18:2.5, width:16, height:16, borderRadius:'50%', background:'#fff', boxShadow:'0 1px 3px rgba(0,0,0,0.2)', transition:'left 0.2s' }} />
    </div>
  )
}

// ── Admin User Drawer ─────────────────────────────────────────────────────────
function AdminDrawer({ admin, allAdmins, onClose, onUpdated, currentUser }) {
  const [role,    setRole]    = useState(admin?.role || 'admin')
  const [perms,   setPerms]   = useState(admin?.permissions || DEFAULT_PERMS[admin?.role] || DEFAULT_PERMS.admin)
  const [saving,  setSaving]  = useState(false)
  const [tab,     setTab]     = useState('permissions')
  const isSelf    = currentUser?.userId === admin?.user_id
  const isSuperAdmin = admin?.role === 'superadmin'

  useEffect(() => {
    setRole(admin?.role || 'admin')
    setPerms(admin?.permissions || DEFAULT_PERMS[admin?.role] || DEFAULT_PERMS.admin)
    setTab('permissions')
  }, [admin?.user_id])

  if (!admin) return null
  const bg = avatarBg(admin.username || admin.phone || '')

  const togglePerm = (key) => {
    if (isSuperAdmin) return
    setPerms(p => ({ ...p, [key]: !p[key] }))
  }

  const applyPreset = (r) => {
    setRole(r)
    setPerms({ ...DEFAULT_PERMS[r] })
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await api.patch(`/roles/admin/admins/${admin.user_id}`, { role, permissions: perms })
      onUpdated(admin.user_id, { role, permissions: perms })
      onClose()
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save')
    } finally { setSaving(false) }
  }

  const handleRevoke = async () => {
    if (!window.confirm(`Remove admin access from ${admin.username}? They will become a regular user.`)) return
    setSaving(true)
    try {
      await api.delete(`/roles/admin/admins/${admin.user_id}`)
      onUpdated(admin.user_id, null)
      onClose()
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to revoke')
    } finally { setSaving(false) }
  }

  const rcfg = rc(role)
  const permCount = Object.values(perms).filter(Boolean).length
  const totalPerms = PERMISSION_GROUPS.flatMap(g => g.permissions).length

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.18)', zIndex:200 }} />
      <div style={{ position:'fixed', top:0, right:0, bottom:0, width:560, background:'#fff', zIndex:201, display:'flex', flexDirection:'column', boxShadow:'-4px 0 40px rgba(0,0,0,0.12)', animation:'slideIn 0.22s ease' }}>
        <style>{`@keyframes slideIn{from{transform:translateX(40px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>

        {/* Header */}
        <div style={{ padding:'18px 22px', borderBottom:'1px solid #e9e9e4', display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ width:44, height:44, borderRadius:10, background:bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:15, fontWeight:800, flexShrink:0 }}>
            {initials(admin.username || admin.phone)}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:14, fontWeight:800 }}>{admin.username || 'Admin User'}</div>
            <div style={{ fontSize:11, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace", marginTop:2 }}>{admin.phone || admin.email || '—'}</div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:6, padding:'4px 10px', borderRadius:6, background:rcfg.bg }}>
            <span style={{ fontSize:14 }}>{rcfg.icon}</span>
            <span style={{ fontSize:11, fontWeight:700, color:rcfg.color, fontFamily:"'JetBrains Mono',monospace" }}>{rcfg.label}</span>
          </div>
          <div onClick={onClose} style={{ cursor:'pointer', fontSize:20, color:'#9a9a94' }}>×</div>
        </div>

        {/* Role picker */}
        {!isSuperAdmin && (
          <div style={{ padding:'12px 22px', borderBottom:'1px solid #e9e9e4', display:'flex', gap:8, flexWrap:'wrap' }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#9a9a94', alignSelf:'center', fontFamily:"'JetBrains Mono',monospace", marginRight:4 }}>PRESET:</div>
            {Object.entries(ROLE_CONFIG).filter(([r]) => r !== 'superadmin').map(([r, cfg]) => (
              <button key={r} onClick={() => applyPreset(r)}
                style={{ padding:'5px 12px', borderRadius:6, border:`1px solid ${role===r?cfg.color:'#e9e9e4'}`, background:role===r?cfg.bg:'#fff', color:role===r?cfg.color:'#6b7280', fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", display:'flex', alignItems:'center', gap:5 }}>
                <span>{cfg.icon}</span>{cfg.label}
              </button>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div style={{ display:'flex', borderBottom:'1px solid #e9e9e4', padding:'0 22px' }}>
          {[['permissions',`Permissions (${permCount}/${totalPerms})`],['info','User Info']].map(([id,label]) => (
            <button key={id} onClick={() => setTab(id)} style={{ padding:'10px 14px', border:'none', background:'none', cursor:'pointer', fontSize:12, fontWeight:700, fontFamily:"'Plus Jakarta Sans',sans-serif", color:tab===id?'#111317':'#9a9a94', borderBottom:`2px solid ${tab===id?'#e8440a':'transparent'}`, marginBottom:-1 }}>{label}</button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex:1, overflowY:'auto', padding:'16px 22px' }}>
          {isSuperAdmin && (
            <div style={{ background:'#f5f3ff', border:'1px solid #ddd6fe', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:12, color:'#7c3aed', fontWeight:600 }}>
              👑 Super Admin has all permissions and cannot be restricted.
            </div>
          )}

          {tab === 'permissions' && (
            PERMISSION_GROUPS.map(group => (
              <div key={group.group} style={{ marginBottom:20 }}>
                <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:10 }}>
                  <span style={{ fontSize:14 }}>{group.icon}</span>
                  <span style={{ fontSize:11, fontWeight:700, color:'#111317', textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:"'JetBrains Mono',monospace" }}>{group.group}</span>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {group.permissions.map(perm => (
                    <div key={perm.key} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'9px 12px', background:perms[perm.key]?'#fafaf8':'#fff', borderRadius:8, border:`1px solid ${perms[perm.key]?'#e9e9e4':'#f3f3f0'}`, transition:'all 0.15s' }}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:'#111317' }}>{perm.label}</div>
                        <div style={{ fontSize:11, color:'#9a9a94', marginTop:1 }}>{perm.description}</div>
                      </div>
                      <Toggle checked={!!perms[perm.key]} onChange={() => togglePerm(perm.key)} disabled={isSuperAdmin || isSelf} />
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}

          {tab === 'info' && (
            <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
              {[
                ['User ID',      admin.user_id],
                ['Username',     admin.username || '—'],
                ['Phone',        admin.phone    || '—'],
                ['Email',        admin.email    || '—'],
                ['Current Role', rc(admin.role).label],
                ['Last Login',   fmtTime(admin.last_login)],
                ['Created',      fmtTime(admin.created_at)],
              ].map(([k,v]) => (
                <div key={k} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid #f3f3f0' }}>
                  <span style={{ fontSize:12, color:'#9a9a94', fontWeight:600 }}>{k}</span>
                  <span style={{ fontSize:12, fontWeight:700, fontFamily:"'JetBrains Mono',monospace", color:'#111317' }}>{v}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {!isSuperAdmin && !isSelf && (
          <div style={{ padding:'14px 22px', borderTop:'1px solid #e9e9e4', display:'flex', gap:10 }}>
            <button onClick={handleRevoke} disabled={saving}
              style={{ height:40, padding:'0 16px', borderRadius:8, border:'1px solid #fecaca', background:'#fef2f2', color:'#b91c1c', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
              Revoke Access
            </button>
            <button onClick={handleSave} disabled={saving}
              style={{ flex:1, height:40, borderRadius:8, border:'none', background:saving?'#f0c4b4':'#e8440a', color:'#fff', fontSize:13, fontWeight:700, cursor:saving?'not-allowed':'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
              {saving ? 'Saving…' : 'Save Permissions'}
            </button>
          </div>
        )}
      </div>
    </>
  )
}

// ── Invite Modal ──────────────────────────────────────────────────────────────
function InviteModal({ onClose, onInvited }) {
  const [form,    setForm]    = useState({ identifier:'', role:'admin' })
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState('')
  const set = (k,v) => setForm(f => ({...f, [k]:v}))

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError('')
    try {
      const res = await api.post('/roles/admin/invite', form)
      onInvited(res.data.admin)
      onClose()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to invite')
    } finally { setLoading(false) }
  }

  const rcfg = rc(form.role)

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.22)', zIndex:300 }} />
      <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:460, background:'#fff', borderRadius:14, zIndex:301, boxShadow:'0 20px 60px rgba(0,0,0,0.15)', animation:'popIn 0.2s ease' }}>
        <style>{`@keyframes popIn{from{opacity:0;transform:translate(-50%,-48%) scale(0.97)}to{opacity:1;transform:translate(-50%,-50%) scale(1)}}`}</style>

        <div style={{ padding:'18px 22px', borderBottom:'1px solid #e9e9e4', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ fontSize:15, fontWeight:800 }}>Add Admin User</div>
          <div onClick={onClose} style={{ cursor:'pointer', fontSize:20, color:'#9a9a94' }}>×</div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding:'18px 22px', display:'flex', flexDirection:'column', gap:16 }}>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#111317', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:6, fontFamily:"'JetBrains Mono',monospace" }}>Phone / Username / Email *</label>
              <input required value={form.identifier} onChange={e => set('identifier', e.target.value)}
                placeholder="Search existing user…"
                style={{ width:'100%', height:42, borderRadius:8, border:'1px solid #e9e9e4', padding:'0 13px', fontSize:13, outline:'none', fontFamily:"'Plus Jakarta Sans',sans-serif", boxSizing:'border-box' }} />
              <div style={{ fontSize:11, color:'#9a9a94', marginTop:4 }}>The user must already have an account on the platform.</div>
            </div>

            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#111317', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:10, fontFamily:"'JetBrains Mono',monospace" }}>Role</label>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {Object.entries(ROLE_CONFIG).filter(([r]) => r !== 'superadmin').map(([r, cfg]) => (
                  <div key={r} onClick={() => set('role', r)}
                    style={{ display:'flex', alignItems:'flex-start', gap:12, padding:'12px 14px', borderRadius:9, border:`1.5px solid ${form.role===r?cfg.color:'#e9e9e4'}`, background:form.role===r?cfg.bg:'#fff', cursor:'pointer', transition:'all 0.15s' }}>
                    <span style={{ fontSize:18, marginTop:1 }}>{cfg.icon}</span>
                    <div>
                      <div style={{ fontSize:13, fontWeight:700, color:form.role===r?cfg.color:'#111317' }}>{cfg.label}</div>
                      <div style={{ fontSize:11, color:'#9a9a94', marginTop:2 }}>{cfg.description}</div>
                    </div>
                    <div style={{ marginLeft:'auto', width:16, height:16, borderRadius:'50%', border:`2px solid ${form.role===r?cfg.color:'#e9e9e4'}`, background:form.role===r?cfg.color:'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      {form.role===r && <div style={{ width:6, height:6, borderRadius:'50%', background:'#fff' }} />}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {error && <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:7, padding:'9px 13px', fontSize:12, color:'#b91c1c', fontWeight:600 }}>⚠ {error}</div>}
          </div>

          <div style={{ padding:'12px 22px 20px', borderTop:'1px solid #e9e9e4', display:'flex', gap:10 }}>
            <button type="button" onClick={onClose} style={{ flex:1, height:42, borderRadius:8, border:'1px solid #e9e9e4', background:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
            <button type="submit" disabled={loading} style={{ flex:1, height:42, borderRadius:8, border:'none', background:loading?'#f0c4b4':'#e8440a', color:'#fff', fontSize:13, fontWeight:700, cursor:loading?'not-allowed':'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
              {loading ? 'Adding…' : `Add as ${rc(form.role).label}`}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Roles() {
  const [admins,   setAdmins]   = useState([])
  const [loading,  setLoading]  = useState(true)
  const [search,   setSearch]   = useState('')
  const [selected, setSelected] = useState(null)
  const [showInvite, setShowInvite] = useState(false)
  const [currentUser, setCurrentUser] = useState(null)

  const fetchAdmins = useCallback(async () => {
    setLoading(true)
    try {
      const [adminRes, meRes] = await Promise.all([
        api.get('/roles/admin/admins'),
        api.get('/user/admin/me').catch(() => ({ data: {} })),
      ])
      setAdmins(adminRes.data.admins || [])
      setCurrentUser(meRes.data.user || null)
    } catch {
      setAdmins([
        { user_id:1, username:'superadmin',  phone:'+250780000001', role:'superadmin', permissions: DEFAULT_PERMS.superadmin, last_login: new Date(Date.now()-3600000).toISOString(),   created_at: new Date(Date.now()-86400000*180).toISOString() },
        { user_id:2, username:'john.admin',   phone:'+250780000002', role:'admin',       permissions: DEFAULT_PERMS.admin,      last_login: new Date(Date.now()-7200000).toISOString(),   created_at: new Date(Date.now()-86400000*90).toISOString()  },
        { user_id:3, username:'sarah.mod',    phone:'+250780000003', role:'moderator',   permissions: DEFAULT_PERMS.moderator,  last_login: new Date(Date.now()-86400000).toISOString(),  created_at: new Date(Date.now()-86400000*60).toISOString()  },
        { user_id:4, username:'support.team', phone:'+250780000004', role:'support',     permissions: DEFAULT_PERMS.support,    last_login: new Date(Date.now()-86400000*2).toISOString(), created_at: new Date(Date.now()-86400000*30).toISOString() },
      ])
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchAdmins() }, [fetchAdmins])

  const handleUpdated = (userId, updates) => {
    if (!updates) {
      setAdmins(prev => prev.filter(a => a.user_id !== userId))
    } else {
      setAdmins(prev => prev.map(a => a.user_id === userId ? { ...a, ...updates } : a))
    }
  }

  const handleInvited = (admin) => {
    setAdmins(prev => [...prev, admin])
  }

  const filtered = admins.filter(a =>
    !search || a.username?.toLowerCase().includes(search.toLowerCase()) || a.phone?.includes(search)
  )

  // Role summary counts
  const roleCounts = admins.reduce((acc, a) => { acc[a.role] = (acc[a.role]||0)+1; return acc }, {})

  return (
    <div style={{ padding:'28px 32px 60px', fontFamily:"'Plus Jakarta Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <div style={{ fontSize:22, fontWeight:800, letterSpacing:'-0.4px' }}>Roles & Permissions</div>
          <div style={{ fontSize:13, color:'#9a9a94', marginTop:4 }}>Manage admin access. Control exactly what each person can see and do.</div>
        </div>
        <button onClick={() => setShowInvite(true)}
          style={{ height:40, padding:'0 20px', borderRadius:9, border:'none', background:'#e8440a', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", boxShadow:'0 2px 8px rgba(232,68,10,0.25)', display:'flex', alignItems:'center', gap:7 }}>
          + Add Admin
        </button>
      </div>

      {/* Role summary cards */}
      <div style={{ display:'flex', gap:12, marginBottom:24, flexWrap:'wrap' }}>
        {Object.entries(ROLE_CONFIG).map(([role, cfg]) => (
          <div key={role} style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, padding:'14px 18px', display:'flex', alignItems:'center', gap:12, minWidth:160 }}>
            <div style={{ width:36, height:36, borderRadius:9, background:cfg.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:17 }}>{cfg.icon}</div>
            <div>
              <div style={{ fontSize:10, fontWeight:600, color:'#9a9a94', textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'JetBrains Mono',monospace" }}>{cfg.label}</div>
              <div style={{ fontSize:22, fontWeight:800, color:cfg.color, letterSpacing:'-0.5px' }}>{roleCounts[role] || 0}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16, background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, padding:'11px 16px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, flex:1, background:'#f6f6f3', border:'1px solid #e9e9e4', borderRadius:7, padding:'7px 12px' }}>
          <span style={{ fontSize:14, color:'#9a9a94' }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by username or phone…"
            style={{ border:'none', outline:'none', background:'transparent', fontSize:13, color:'#111317', flex:1, fontFamily:"'Plus Jakarta Sans',sans-serif" }} />
          {search && <span onClick={() => setSearch('')} style={{ cursor:'pointer', color:'#9a9a94', fontSize:16 }}>×</span>}
        </div>
        <div style={{ fontSize:12, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace", whiteSpace:'nowrap' }}>{filtered.length} admin{filtered.length!==1?'s':''}</div>
      </div>

      {/* Admin list */}
      {loading ? (
        <div style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, padding:'48px 0', textAlign:'center', color:'#9a9a94', fontSize:13 }}>Loading…</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {filtered.map(admin => {
            const cfg = rc(admin.role)
            const permCount = Object.values(admin.permissions||{}).filter(Boolean).length
            const totalPerms = PERMISSION_GROUPS.flatMap(g=>g.permissions).length
            const bg = avatarBg(admin.username || admin.phone || '')
            return (
              <div key={admin.user_id}
                onClick={() => setSelected(admin)}
                style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, padding:'16px 20px', cursor:'pointer', display:'flex', alignItems:'center', gap:14, transition:'all 0.12s' }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.07)'; e.currentTarget.style.borderColor=cfg.color+'44' }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow='none'; e.currentTarget.style.borderColor='#e9e9e4' }}>

                {/* Avatar */}
                <div style={{ width:42, height:42, borderRadius:10, background:bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:800, flexShrink:0 }}>
                  {initials(admin.username || admin.phone)}
                </div>

                {/* Name + meta */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:800, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{admin.username || 'Admin'}</div>
                  <div style={{ fontSize:11, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace", marginTop:2 }}>{admin.phone || admin.email || '—'}</div>
                </div>

                {/* Permission bar */}
                <div style={{ minWidth:120 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                    <span style={{ fontSize:10, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>permissions</span>
                    <span style={{ fontSize:10, fontWeight:700, color:'#111317', fontFamily:"'JetBrains Mono',monospace" }}>{permCount}/{totalPerms}</span>
                  </div>
                  <div style={{ width:'100%', height:4, background:'#f3f3f0', borderRadius:2 }}>
                    <div style={{ width:`${(permCount/totalPerms)*100}%`, height:'100%', background:cfg.color, borderRadius:2 }} />
                  </div>
                </div>

                {/* Role badge */}
                <div style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 12px', borderRadius:6, background:cfg.bg, flexShrink:0 }}>
                  <span style={{ fontSize:13 }}>{cfg.icon}</span>
                  <span style={{ fontSize:11, fontWeight:700, color:cfg.color, fontFamily:"'JetBrains Mono',monospace" }}>{cfg.label}</span>
                </div>

                {/* Last login */}
                <div style={{ textAlign:'right', minWidth:90, flexShrink:0 }}>
                  <div style={{ fontSize:10, color:'#9a9a94', fontFamily:"'JetBrains Mono',monospace" }}>Last login</div>
                  <div style={{ fontSize:11, fontWeight:600, color:'#374151', marginTop:2 }}>
                    {admin.last_login ? new Date(admin.last_login).toLocaleDateString('en-GB', { day:'2-digit', month:'short' }) : '—'}
                  </div>
                </div>

                <div style={{ color:'#9a9a94', fontSize:14 }}>›</div>
              </div>
            )
          })}

          {filtered.length === 0 && (
            <div style={{ background:'#fff', border:'1px solid #e9e9e4', borderRadius:10, padding:'48px 0', textAlign:'center', color:'#9a9a94', fontSize:13 }}>
              No admins found.
            </div>
          )}
        </div>
      )}

      {/* Drawers & modals */}
      <AdminDrawer
        admin={selected}
        onClose={() => setSelected(null)}
        onUpdated={handleUpdated}
        currentUser={currentUser}
      />
      {showInvite && (
        <InviteModal onClose={() => setShowInvite(false)} onInvited={handleInvited} />
      )}
    </div>
  )
}